import { useState, useEffect, lazy, Suspense, type ReactNode } from 'react';
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { loginRequest } from './lib/msalConfig';
import { usersApi, impersonation } from './lib/apiClient';
import { rememberFeedbackLink } from './lib/feedbackMine';
import { visibleTabs, todayRouteState, adminRouteState, type NavAccess } from './navAccess';
import { isFieldLeaderOnly as isFieldLeaderOnlyFn } from './lib/access';
import HelpDrawer, { openHelp } from './components/HelpDrawer';
import TrainingPage from './modules/training/TrainingPage';
import WhatsNew, { type WhatsNewMe } from './modules/whatsnew/WhatsNew';
import { rememberWhatsNewLink } from './modules/whatsnew/lib/link';
import FeedbackButton from './components/FeedbackButton';

import ProjectsPage  from './modules/projects/ProjectsPage';
import ProjectWorkspace from './modules/projects/ProjectWorkspace';
import ProjectByJobRedirect from './modules/projects/ProjectByJobRedirect';
import ScheduleRollup from './modules/rollups/ScheduleRollup';
import SafetyRollup   from './modules/rollups/SafetyRollup';
import DailyLogsRollup from './modules/rollups/DailyLogsRollup';
import EquipmentPage from './modules/equipment/EquipmentPage';
import ReceivingPage from './modules/receiving/ReceivingPage';
import ShippingPage from './modules/shipping/ShippingPage';
import QaqcRollupPage from './modules/qaqc/QaqcRollupPage';
import ManpowerPage  from './modules/manpower/ManpowerPage';
import SurveyPage    from './modules/survey/SurveyPage';
import ReportsPage   from './modules/reports/ReportsPage';
import AdminPage     from './modules/admin/AdminPage';
import PprPage       from './modules/ppr/PprPage';
import ContractsPage from './modules/contracts/ContractsPage';
import {
  NewSubcontractRequestPage, EditSubcontractRequestPage, SubcontractRequestPage,
} from './modules/contracts/SubcontractRequestPages';
// ⚠️ Rendered OUTSIDE the router and outside the MSAL templates — see App().
import TravelAcknowledgmentPage from './modules/travel/TravelAcknowledgmentPage';
import { acknowledgmentTokenFrom } from './lib/publicRoutes';

const css = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: var(--ew-font); background: var(--ew-n-50); color: var(--ew-dark); font-size: var(--ew-fs-body-sm); }
  a { text-decoration: none; color: inherit; }

  /* ── App shell ──────────────────────────────────────────────────── */
  .app-header {
    background: var(--ew-dark);
    color: #fff;
    height: 56px;
    display: flex;
    align-items: center;
    padding: 0 var(--ew-s-5);
    gap: var(--ew-s-5);
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: var(--ew-shadow-1);
  }
  .app-header-logo {
    height: 26px;
    width: auto;
    display: block;
    flex-shrink: 0;
  }
  .app-header-divider {
    width: 1px;
    height: 20px;
    background: rgba(255,255,255,0.15);
    flex-shrink: 0;
  }
  .app-header-product {
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
    color: rgba(255,255,255,0.75);
    white-space: nowrap;
  }
  .app-header-right {
    display: flex;
    align-items: center;
    gap: var(--ew-s-3);
    margin-left: auto;
    font-size: var(--ew-fs-body-sm);
    color: rgba(255,255,255,0.7);
  }

  /* ── Nav tabs ───────────────────────────────────────────────────── */
  .app-tabs {
    display: flex;
    background: #fff;
    border-bottom: 1px solid var(--ew-n-200);
    padding: 0 var(--ew-s-5);
    /* Stick just below the 56px app header so the module tabs stay
       reachable while scrolling long tables. */
    position: sticky;
    top: 56px;
    z-index: 99;
    /* Scroll the bar rather than overflow the page when the tabs don't fit
       (e.g. narrow laptops / iPad landscape). No effect on a wide desktop. */
    overflow-x: auto;
    scrollbar-width: none;
  }
  .app-tabs::-webkit-scrollbar { display: none; }
  .app-tab {
    padding: 14px var(--ew-s-4);
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
    letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase;
    color: var(--ew-n-500);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition: color var(--ew-dur-fast) var(--ew-ease),
                border-color var(--ew-dur-fast) var(--ew-ease);
    white-space: nowrap;
  }
  .app-tab:hover { color: var(--ew-dark); }
  .app-tab.active { color: var(--ew-dark); border-bottom-color: var(--ew-green); }

  /* ── Page content ───────────────────────────────────────────────── */
  .app-content { padding: var(--ew-s-5); }

  /* ── Shared button primitives ───────────────────────────────────── */
  .btn {
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
    border: 1px solid transparent;
    padding: 8px 14px;
    border-radius: var(--ew-r-md);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: background var(--ew-dur-fast) var(--ew-ease),
                border-color var(--ew-dur-fast) var(--ew-ease);
    font-family: var(--ew-font);
  }
  .btn-primary  { background: var(--ew-green); color: #fff; }
  .btn-primary:hover  { background: var(--ew-green-700); }
  .btn-secondary { background: #fff; color: var(--ew-dark); border-color: var(--ew-n-300); }
  .btn-secondary:hover { border-color: var(--ew-dark); }
  .btn-ghost { background: transparent; color: rgba(255,255,255,0.75); border-color: rgba(255,255,255,0.2); }
  .btn-ghost:hover { color: #fff; border-color: rgba(255,255,255,0.4); }

  /* ── Help "?" button (header) ───────────────────────────────────── */
  .help-btn {
    width: 26px; height: 26px; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: var(--ew-r-pill);
    background: transparent; color: rgba(255,255,255,0.75);
    border: 1px solid rgba(255,255,255,0.25);
    font: var(--ew-fw-semibold) 14px/1 var(--ew-font);
    cursor: pointer;
    transition: color var(--ew-dur-fast) var(--ew-ease),
                border-color var(--ew-dur-fast) var(--ew-ease),
                background var(--ew-dur-fast) var(--ew-ease);
  }
  .help-btn:hover { color: #fff; border-color: var(--ew-green); background: rgba(2,120,53,0.18); }
  .btn-danger { background: var(--ew-danger); color: #fff; }
  .btn-sm { font-size: var(--ew-fs-meta); padding: 4px 10px; }
  .btn[disabled] { opacity: 0.45; cursor: not-allowed; }

  /* ── Login screen ───────────────────────────────────────────────── */
  .login-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: var(--ew-n-50);
  }
  .login-card {
    background: #fff;
    border: 1px solid var(--ew-n-200);
    border-radius: var(--ew-r-lg);
    padding: var(--ew-s-7) var(--ew-s-6);
    text-align: center;
    box-shadow: var(--ew-shadow-3);
    max-width: 360px;
    width: 100%;
  }
  .login-card-logo {
    height: 32px;
    margin-bottom: var(--ew-s-5);
  }
  .login-card h1 {
    font: var(--ew-fw-semibold) var(--ew-fs-h4)/1.2 var(--ew-font);
    color: var(--ew-dark);
    margin-bottom: var(--ew-s-2);
  }
  .login-card p {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/var(--ew-lh-body) var(--ew-font);
    color: var(--ew-n-500);
    margin-bottom: var(--ew-s-5);
  }

  /* ── Mobile nav (hamburger) ─────────────────────────────────────────
     Desktop keeps the full horizontal tab bar untouched. On phones/small
     tablets the 9–10 uppercase tabs no longer fit, so we collapse them
     behind a hamburger that drops the same links down as a tap list. */
  .app-mobnav { display: none; }            /* hidden on desktop */
  .app-nav-toggle {
    display: inline-flex; align-items: center; gap: var(--ew-s-3);
    width: 100%; height: 100%; padding: 0 var(--ew-s-5);
    background: transparent; border: 0; cursor: pointer;
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1 var(--ew-font);
    letter-spacing: var(--ew-ls-caption); text-transform: uppercase;
    color: var(--ew-dark);
  }
  .app-nav-burger { display: inline-flex; flex-direction: column; gap: 4px; }
  .app-nav-burger span { width: 18px; height: 2px; background: var(--ew-dark); border-radius: 2px; }
  .app-nav-current { color: var(--ew-n-500); }

  /* Nav collapses to a hamburger up through iPad PORTRAIT (<=1024px). iPad
     landscape (1080px+) and laptops keep the full horizontal tab bar. This
     1024 line maps cleanly to "portrait → menu, landscape → tabs". */
  @media (max-width: 1024px) {
    /* The mobile menu bar replaces the tab row; sticks under the header. */
    .app-mobnav {
      display: flex; align-items: center; height: 48px;
      background: #fff; border-bottom: 1px solid var(--ew-n-200);
      position: sticky; top: 56px; z-index: 99;
    }
    /* Collapse the tab row; reveal it as a dropdown overlay when open. */
    .app-tabs {
      display: none; padding: 0; overflow-x: visible;
      position: fixed; top: 104px; left: 0; right: 0;
      flex-direction: column; align-items: stretch;
      max-height: calc(100vh - 104px); overflow-y: auto;
      box-shadow: var(--ew-shadow-3);
    }
    .app-tabs.open { display: flex; }
    .app-tab {
      padding: 15px var(--ew-s-5); margin-bottom: 0;
      border-bottom: 1px solid var(--ew-n-100);
      border-left: 3px solid transparent;
    }
    .app-tab.active { border-bottom-color: var(--ew-n-100); border-left-color: var(--ew-green); }
  }

  /* Header trim + tighter gutters are phone-only — an iPad has room to keep
     the product name and signed-in user in the header. */
  @media (max-width: 768px) {
    .app-header { padding: 0 var(--ew-s-4); gap: var(--ew-s-3); }
    .app-header-divider, .app-header-product, .app-header-right > span { display: none; }
    .app-content { padding: var(--ew-s-4); }
  }
  @media (max-width: 480px) {
    .app-content { padding: var(--ew-s-3); }
  }

  /* ── Impersonation ("View as") banner ───────────────────────────────
     Sticky at the very top (in flow, so it pushes the header down). While
     impersonating, the sticky header/tabs offsets shift down by its height so
     nothing overlaps. */
  .imp-banner {
    position: sticky; top: 0; z-index: 300;
    height: 34px; display: flex; align-items: center; gap: var(--ew-s-3);
    padding: 0 var(--ew-s-5); background: #B5611A; color: #fff;
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font);
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  }
  .imp-banner b { font-weight: 700; }
  .imp-banner .imp-sub { color: rgba(255,255,255,0.8); }
  .imp-banner .imp-exit {
    margin-left: auto; background: rgba(255,255,255,0.15); color: #fff;
    border: 1px solid rgba(255,255,255,0.5); border-radius: var(--ew-r-md);
    padding: 4px 12px; cursor: pointer; font: var(--ew-fw-semibold) var(--ew-fs-meta)/1 var(--ew-font);
  }
  .imp-banner .imp-exit:hover { background: rgba(255,255,255,0.28); }
  body.impersonating .app-header { top: 34px; }
  body.impersonating .app-tabs   { top: 90px; }
  @media (max-width: 1024px) {
    body.impersonating .app-mobnav { top: 90px; }
    body.impersonating .app-tabs   { top: 138px; }
  }
`;

function LoginScreen() {
  const { instance } = useMsal();
  // A feedback email's link (`?feedback=mine`) is very likely to land on THIS screen, and signing in is a redirect to Microsoft and
  // back. Remember it here, the one place shown only to somebody who is not signed in, so the drawer can still open on My feedback
  // afterwards whether or not the way back keeps the query string. See `lib/feedbackMine`.
  // The weekly What's new email's link (`?whatsnew=1`) has the same problem, and the same answer.
  useEffect(() => {
    rememberFeedbackLink(window.location.search);
    rememberWhatsNewLink(window.location.search);
  }, []);
  return (
    <div className="login-screen">
      <div className="login-card">
        <img src="/ew-logo-horizontal-green.png" alt="East &amp; Westbrook" className="login-card-logo" />
        <h1>Project Tracker</h1>
        <p>Sign in with your company account to continue.</p>
        <button
          className="btn btn-primary"
          style={{ width: '100%', padding: '11px 0', justifyContent: 'center', fontSize: 'var(--ew-fs-body-ui)' }}
          onClick={() => instance.loginRedirect(loginRequest)}
        >
          Sign in with Microsoft
        </button>
      </div>
    </div>
  );
}

function Header({ me }: { me: WhatsNewMe }) {
  const { instance, accounts } = useMsal();
  const user = accounts[0];
  return (
    <header className="app-header">
      <img src="/ew-logo-horizontal-white.png" alt="East &amp; Westbrook" className="app-header-logo" />
      <div className="app-header-divider" />
      <span className="app-header-product">Project Tracker</span>
      <div className="app-header-right">
        {/* In-app feedback — captures the page + (server-side) who/role. */}
        <FeedbackButton />
        {/* Recent changes for this person's role, linked to the training guide. */}
        <WhatsNew me={me} />
        {/* Always-available help — opens the access-aware help drawer
            (mounted in AuthenticatedShell, which holds `me`). */}
        <button
          className="help-btn"
          title="Help — what you can do in the Project Tracker"
          aria-label="Open Project Tracker help"
          onClick={() => openHelp()}
        >
          ?
        </button>
        <span>{user?.name ?? user?.username}</span>
        <button className="btn btn-ghost" onClick={() => instance.logoutRedirect()}>
          Sign out
        </button>
      </div>
    </header>
  );
}

/** Read-only "View as" banner — shown whenever the API confirms impersonation. */
function ImpersonationBanner({ name, realName }: { name: string | null; realName: string | null }) {
  return (
    <div className="imp-banner" role="alert">
      <span>👁 Viewing as <b>{name ?? 'user'}</b> — <b>read-only</b>.</span>
      <span className="imp-sub">You're signed in as {realName ?? 'admin'}.</span>
      <button className="imp-exit" onClick={() => impersonation.stop()}>Exit "View as"</button>
    </div>
  );
}

/**
 * Route-split like the other heavy pages.
 *
 * ⚠️ This used to be `TODAY_ENABLED = import.meta.env.DEV`, which kept the page
 * out of production builds entirely. That was never about the feature being
 * unfinished — it was about `todayMock.ts`, the sample data the screen ran on,
 * which was built from real customer project names and their Sage job numbers.
 * `vite.config.ts` sets `sourcemap: true` and deploy.yml uploads the whole of
 * `apps/web/dist`, so any module in the graph has its original TypeScript
 * written into `dist/assets/index-*.js.map` and served unauthenticated — which
 * is how the mock got published the first time, with Rollup having shaken the
 * code itself out of the bundle.
 *
 * #509 wired the page to the database and deleted the mock, so there is nothing
 * left in this module to keep out of the graph. The lesson outlives the flag,
 * though: fixture data with real names in ANY module reachable from here is
 * published, whether or not the code ships.
 *
 * Who sees the page is now a data question — see `canViewToday`.
 */
const TodayPage = lazy(() => import('./modules/today/TodayPage'));
// Route-split the same way, and separately: the two pages share no data and a
// leadership viewer should not download the PM page's bundle to reach theirs.
const LeadershipTodayPage = lazy(() => import('./modules/today/LeadershipTodayPage'));

/**
 * Which Today page `/today` renders — decided INSIDE the router.
 *
 * ⚠️ IT HAS TO BE A COMPONENT, not a value computed in `AuthenticatedShell`.
 * That function sits ABOVE the `<BrowserRouter>` it returns, so a
 * `window.location.search` read there does not subscribe to router navigation:
 * clicking the in-app switch would change the URL while this value stayed put,
 * leaving the wrong page mounted until some unrelated re-render or a full
 * refresh. `useLocation()` is what makes the switch actually switch.
 * (Codex, P2 on #513 — my bug, and it broke the "← My work" link I had already
 * shipped on the leadership page.)
 *
 * The DECISION itself still lives in `todayRouteState`, pure and tested. This
 * only supplies the location and renders the answer — the lesson of #510, where
 * the route half of the gate was left inline and untested while the tab half
 * was extracted.
 */
function TodayRoute(props: {
  meLoaded: boolean;
  isFieldLeaderOnly: boolean;
  canViewToday: boolean;
  canViewLeadershipToday: boolean;
}) {
  const { search } = useLocation();
  const requestedView =
    new URLSearchParams(search).get('view') === 'company' ? ('company' as const) : null;
  const state = todayRouteState({ ...props, requestedView });

  if (state === 'loading') return null;
  if (state === 'redirect') return <Navigate to="/projects" replace />;
  return (
    <Suspense fallback={null}>
      {state === 'leadership' ? <LeadershipTodayPage /> : <TodayPage />}
    </Suspense>
  );
}

function Navigation(access: NavAccess) {
  const tabs = visibleTabs(access);

  // Mobile-only collapsed state. On desktop the dropdown CSS is inert (the
  // hamburger bar is display:none and the tab row is always shown), so this
  // state simply has no visual effect there.
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  // Close the menu whenever the route changes (i.e. after a tap).
  useEffect(() => { setMenuOpen(false); }, [pathname]);
  // Label the hamburger with the section you're currently in.
  const current = tabs.find((t) => pathname.startsWith(t.path));

  return (
    <>
      {/* Mobile menu bar — only visible <=768px (see .app-mobnav). */}
      <div className="app-mobnav">
        <button
          type="button"
          className="app-nav-toggle"
          aria-expanded={menuOpen}
          aria-label="Toggle navigation menu"
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span className="app-nav-burger" aria-hidden="true"><span /><span /><span /></span>
          Menu
          {current ? <span className="app-nav-current">· {current.label}</span> : null}
        </button>
      </div>

      <nav className={`app-tabs${menuOpen ? ' open' : ''}`}>
        {tabs.map((tab) => (
          <NavLink
            key={tab.path}
            to={tab.path}
            className={({ isActive }) => `app-tab${isActive ? ' active' : ''}`}
            onClick={() => setMenuOpen(false)}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
    </>
  );
}

/** Inner shell rendered only when authenticated — can safely call React Query. */
function AuthenticatedShell() {
  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => usersApi.me(),
    staleTime: 5 * 60 * 1000,
    /**
     * ⚠️ A BACKSTOP for `hasActiveAssignments`, not a substitute for
     * invalidating it. `invalidateAssignmentQueries` corrects the tab the
     * instant YOU change your own assignments; this catches the changes no
     * client mutation can see.
     *
     * The bid webhook creates SYSTAC/ClosedWon placeholders carrying an
     * `estimator_id` and archives them again, entirely server-side
     * (`projects.service.ts`), so an estimator's first or last assignment can
     * appear or vanish with nothing happening in their browser. Another admin
     * putting them on a job does the same. Nothing else would ever refetch
     * this: `refetchOnWindowFocus` is false globally and this shell mounts
     * once per session, so without a poll the tab stays wrong for as long as
     * the tab is open. (Codex, #510.)
     *
     * Five minutes, matching the Today page's own refetch. It does not run
     * while the tab is hidden (`refetchIntervalInBackground` defaults false),
     * and `/users/me` is one indexed EXISTS.
     */
    refetchInterval: 5 * 60 * 1000,
  });

  const isAdmin = me?.roles.includes('Admin') ?? false;
  // The Feedback Admin role: opens the Admin tab, which then offers Feedback alone.
  // Read straight off `me.permissions`; Admin reaches the same tab through `isAdmin`.
  const canManageFeedback = me?.permissions.includes('feedback.manage') ?? false;
  const canViewReports = isAdmin || (me?.permissions.includes('reports.view') ?? false);
  // PPR tab is assignable: ppr.view (view-only) OR ppr.manage (managers keep it).
  const canViewPpr = isAdmin || (me?.permissions.includes('ppr.view') ?? false) || (me?.permissions.includes('ppr.manage') ?? false);
  // Contracts tab is assignable: contracts.view (read) or contracts.edit, which also reads.
  const canViewContracts = isAdmin || (me?.permissions.includes('contracts.view') ?? false) || (me?.permissions.includes('contracts.edit') ?? false);
  // A subcontract request (#627) opens for whoever may start one or work the queue. The API decides each request.
  const canOpenSubcontractRequests = canViewContracts || (me?.permissions.includes('subcontracts.request') ?? false);
  // The Shipping page lists every job's requests, with onsite contacts' phone numbers, so it opens for whoever the API
  // lets read across jobs: receiving.edit (Logistics, and the PMs who raise requests), or view-all (Admin included).
  // `me.canViewAll` is the app's own canViewAllProjects. The API is the real gate; this only stops an empty shell.
  const canViewShipping = isAdmin || (me?.canViewAll ?? false) || (me?.permissions.includes('receiving.edit') ?? false);
  /**
   * Today is shown to whoever the page has something to say to — anybody named
   * on a live project. Computed in SQL from the same predicate as the header's
   * "Your N active projects", so the tab and the page cannot disagree; see
   * `hasActiveAssignments` in `today.service.ts`.
   *
   * ⚠️ NOT a permission, and deliberately not a role. The endpoint behind the
   * page carries no `@RequirePermission` because everything it returns is
   * already scoped to the caller's own assignments — so somebody who reaches
   * /today without any is not seeing something they should not, they are
   * seeing an empty screen with their name on it. And there is no `PM` role to
   * test against: being a PM here is an assignment, not a badge.
   *
   * ⚠️ `isAdmin` deliberately does NOT override this. An admin with no jobs of
   * their own gets the same empty page as anybody else, and Today is not an
   * administrative screen.
   */
  const canViewToday = me?.hasActiveAssignments ?? false;
  /**
   * The leadership Today view — a company-wide roll-up organized by project
   * manager, behind its own assignable permission.
   *
   * ⚠️ A SEPARATE REASON TO SEE THE TAB, not a widening of the rule above.
   * `hasActiveAssignments` is pure assignment by design, which is why it hides
   * the page from the very people this view is for: measured on production
   * 2026-09-19, eight of the eleven leadership-role holders had no tab and no
   * route at all. Loosening that rule would instead start showing an empty
   * personal page to people with no jobs, so the two stay independent and are
   * OR'd in `visibleTabs`.
   *
   * Read straight off `me.permissions` — `/me` already carries them, so this
   * costs no extra request on a path every page load waits for.
   */
  const canViewLeadershipToday = me?.permissions?.includes('today.leadership') ?? false;
  const impersonating = me?.impersonating ?? false;

  // Field-leader-only users (FieldLeader role, none of the office grants) get a
  // single-tab experience: only Projects — which the server already scopes to
  // their assignments. The cross-project roll-up tabs (Schedule, Manpower,
  // Equipment, Survey, Receiving, QA/QC, Safety, Daily Logs) are hidden and their routes
  // redirect to /projects. Mirrors isFieldLeaderScoped() on the API.
  // A field leader stays restricted unless they're genuinely office — a PM/PE
  // (projects.edit), an exec (view-all), or Admin. reports.view (which the
  // FieldLeader role may incidentally carry) does NOT exempt them.
  // Defined once in lib/access and shared with ProjectWorkspace and ChangesTab,
  // so the nav gate and the per-screen gates can't drift apart.
  const isFieldLeaderOnly = isFieldLeaderOnlyFn(me);
  const adminRoute = adminRouteState({
    meLoaded: me !== undefined, isFieldLeaderOnly, isAdmin, canManageFeedback,
  });
  // Wait for `me` before redirecting a roll-up route, so a direct link/refresh
  // for a permitted user isn't bounced by a premature (pre-load) decision.
  const flGuard = (el: ReactNode) =>
    me === undefined ? null : isFieldLeaderOnly ? <Navigate to="/projects" replace /> : el;
  // Shift the sticky header/tabs down while the impersonation banner is shown.
  useEffect(() => {
    document.body.classList.toggle('impersonating', impersonating);
    return () => document.body.classList.remove('impersonating');
  }, [impersonating]);

  return (
    <BrowserRouter>
      {impersonating && <ImpersonationBanner name={me?.displayName ?? null} realName={me?.realName ?? null} />}
      <Header me={me} />
      {/* Access-aware help drawer; opened by the header "?" button via openHelp(). */}
      <HelpDrawer me={me} />
      <Navigation isAdmin={isAdmin} canManageFeedback={canManageFeedback} canViewReports={canViewReports} canViewPpr={canViewPpr} canViewContracts={canViewContracts} canViewShipping={canViewShipping} canViewToday={canViewToday} canViewLeadershipToday={canViewLeadershipToday} isFieldLeaderOnly={isFieldLeaderOnly} />
      <main className="app-content">
        <Routes>
          <Route path="/" element={<Navigate to="/projects" replace />} />
          {/* Personalized landing screen, behind the two gates that also hide
              the tab: field-leader-only users keep their single-tab experience,
              and anybody with no live project assignment has nothing to see, so
              they go to Projects rather than an empty page with their name on
              it. Guarded here as well as in the nav because the tab being
              absent does not stop anyone typing the URL or following an old
              bookmark. The decision itself is `todayRouteState`, which is pure
              and tested; this only renders it. */}
          <Route
            path="/today"
            element={
              <TodayRoute
                meLoaded={me !== undefined}
                isFieldLeaderOnly={isFieldLeaderOnly}
                canViewToday={canViewToday}
                canViewLeadershipToday={canViewLeadershipToday}
              />
            }
          />
          <Route path="/projects"     element={<ProjectsPage />}     />
          {/* External deep-link entry (e.g. from the Safety Site, which only
              knows job numbers) — resolves to /projects/:id. */}
          <Route path="/projects/by-job/:jobNumber" element={<ProjectByJobRedirect />} />
          <Route path="/projects/:id" element={<ProjectWorkspace />} />
          {/* Open to every signed-in user, field leaders included: the page
              filters its own content by access, and the people with the
              narrowest view are the ones who most need the guide. */}
          <Route path="/training" element={<TrainingPage />} />
          <Route path="/schedule" element={flGuard(<ScheduleRollup />)} />
          <Route path="/safety"   element={flGuard(<SafetyRollup />)} />
          <Route path="/daily-logs" element={flGuard(<DailyLogsRollup />)} />
          <Route path="/equipment/*" element={flGuard(<EquipmentPage />)} />
          <Route path="/receiving/*" element={flGuard(<ReceivingPage />)} />
          {/* Gated like /contracts: wait for `me`, keep field leaders out, then the permission. A job's own requests
              are on its Shipping tab, which everyone who can see the job reads. */}
          <Route path="/shipping" element={flGuard(canViewShipping ? <ShippingPage /> : <Navigate to="/projects" replace />)} />
          <Route path="/qaqc" element={flGuard(<QaqcRollupPage />)} />
          <Route path="/manpower/*"  element={flGuard(<ManpowerPage />)}  />
          <Route path="/survey/*"    element={flGuard(<SurveyPage />)}    />
          {/* Same shape as /ppr: wait for `me`, keep field leaders out, then gate on
              the permission. The API is the real gate; this only stops a typed URL
              from rendering an empty shell. */}
          <Route
            path="/contracts"
            element={flGuard(canViewContracts ? <ContractsPage /> : <Navigate to="/projects" replace />)}
          />
          {/* A subcontract request: the form to start or change one, and the page that reads one back. Same guards as
              /contracts above; the API is the real gate, and a request the viewer may not see is a 404 there. */}
          <Route
            path="/contracts/requests/new/:projectId"
            element={flGuard(canOpenSubcontractRequests ? <NewSubcontractRequestPage /> : <Navigate to="/projects" replace />)}
          />
          <Route
            path="/contracts/requests/:id/edit"
            element={flGuard(canOpenSubcontractRequests ? <EditSubcontractRequestPage /> : <Navigate to="/projects" replace />)}
          />
          <Route
            path="/contracts/requests/:id"
            element={flGuard(canOpenSubcontractRequests ? <SubcontractRequestPage /> : <Navigate to="/projects" replace />)}
          />
          {/* flGuard waits for `me`, then redirects field-leader-only users;
              otherwise gates on ppr.view / ppr.manage. */}
          <Route
            path="/ppr/*"
            element={flGuard(canViewPpr ? <PprPage /> : <Navigate to="/projects" replace />)}
          />
          {/* Render nothing until `me` resolves so a permitted user refreshing
              /reports isn't bounced to /projects by a premature redirect. */}
          <Route
            path="/reports/*"
            element={flGuard(canViewReports ? <ReportsPage /> : <Navigate to="/projects" replace />)}
          />
          {/* ALWAYS registered, and decided inside the element — like /reports and
              /contracts above. Registering it only once `me` had loaded meant a
              refresh or bookmark on /admin matched the redirect before the answer
              arrived and landed on /projects. The decision is `adminRouteState`,
              which is pure and tested; this only renders it. The page itself
              decides WHICH sub-tabs this viewer gets. */}
          <Route
            path="/admin/*"
            element={
              adminRoute === 'loading' ? null
                : adminRoute === 'page' ? <AdminPage isAdmin={isAdmin} />
                : <Navigate to="/projects" replace />
            }
          />
        </Routes>
      </main>
    </BrowserRouter>
  );
}

/**
 * The one path that must render for somebody with no account.
 *
 * ⚠️ IT IS MATCHED ABOVE THE MSAL TEMPLATES, NOT INSIDE THE ROUTER, and that
 * placement is the whole feature. `<BrowserRouter>` lives inside
 * `AuthenticatedShell`, so a route added to it is only ever reached by a
 * signed-in user; a signed-out visitor falls to `<UnauthenticatedTemplate>` and
 * gets `<LoginScreen />`. That is exactly the screen a texted acknowledgment
 * link must never show — the person holding it has never opened the Project
 * Tracker and has nothing to sign in with.
 *
 * ⚠️ AND IT RENDERS ALONE: no Header, no Navigation, no HelpDrawer, no
 * FeedbackButton, and no `/me` request. Every one of those assumes a signed-in
 * user, and the page deliberately declares all of its own CSS because of it
 * (see the note on `css` in TravelAcknowledgmentPage).
 *
 * Reading `window.location` directly is safe HERE and would not be inside the
 * router: there is no client-side navigation to or from this page. Every visit
 * is a fresh load of a link from a text message, and the page has no links out.
 *
 * ⚠️ THE PATH DECIDES, NOT THE TOKEN'S SHAPE. This used to require the exact
 * 43-character base64url form, so a link with a trailing `.` from an SMS link
 * detector fell through to the templates and showed the traveller the sign-in
 * screen. `lib/publicRoutes` explains that at length; the short version is
 * that judging the token is the page's job, because the page is what can say
 * "this link is not valid" in the reader's language.
 */
export default function App() {
  const ackToken = acknowledgmentTokenFrom(window.location.pathname);
  /**
   * Imported eagerly rather than route-split, deliberately. The signer is on a
   * phone on a link from a text message and is already fetching the main
   * bundle that contains this check; lazy-loading the page would buy the app's
   * regular users nothing measurable and cost the one person who came here for
   * this page a second round trip before they see anything.
   */
  if (ackToken) return <TravelAcknowledgmentPage token={ackToken} />;

  return (
    <>
      <style>{css}</style>
      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <AuthenticatedShell />
      </AuthenticatedTemplate>
    </>
  );
}
